import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Axios from "axios";
import { DayOfWeek, Pivot, PivotItem, SharedColors, Stack, Text } from "@fluentui/react";
import { EInvoiceSettingsForm } from "../components/EInvoiceSettingsForm";
import { useTenantResourceSingle } from "../hooks/useTenantResourceSingle";
import {
  CompanySettings,
  CompanySettingsWorkHours,
  CompanySettingsReturnDto,
  OnixTenant,
  TeamViewerGroupsDtoGroup,
} from "../API";
import { TenantResource } from "../enums/TenantResource";
import {
  BlobElement,
  ButtonElement,
  CheckboxElement,
  CustomRenderElement,
  CustomSearchBoxElement,
  EnumDropdownElement,
  GroupElement,
  ITenantFormLayout,
  MultilineTextElement,
  NumberElement,
  RichTextElement,
  TextFieldElement,
  TimeElement,
  TitleElement,
} from "../components/OnixElements";
import { DownloadArbitrary } from "../helpers/DownloadHelper";
import { GetApiError } from "../components/ShowApiError";
import { AppRoute } from "../AppRoutes";
import { TenantForm } from "../components/TenantForm";
import { CompanyApplicationPackets } from "./CompanyApplicationPackets";
import { CompanyForwardEmailsSection } from "../components/CompanyForwardEmailsSection";
import { CompanyLabelCompositionsSection } from "../components/CompanyLabelCompositionsSection";
import { CompanyAiSupportSettingsForm } from "../components/CompanyAiSupportSettingsForm";
import { TenantList } from "../components/TenantList";
import { MakeTenantColumns } from "../helpers/MakeTenantColumns";

export const AdminEditCompanySettingsSection: FC = () => {
  const { id } = useParams();

  const companyId = useMemo(() => (id ? +id : undefined), [id]);

  const { search } = useLocation();
  const { model, dto, loadModel, action } = useTenantResourceSingle<
    CompanySettings,
    CompanySettings,
    CompanySettingsReturnDto
  >(TenantResource.AdminSingleCompanyTenant);
  const [appHost, setAppHost] = useState<string>();
  const history = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const companyWorkHoursBaseDto = useMemo<Partial<CompanySettingsWorkHours>>(
    () => ({
      companySettingsId: companyId,
    }),
    [companyId],
  );
  const companyWorkHoursQuery = useMemo(
    () =>
      companyId
        ? {
            companySettingsId: {
              Equals: companyId,
            },
          }
        : undefined,
    [companyId],
  );
  const companyWorkHoursColumns = useMemo(
    () =>
      MakeTenantColumns<CompanySettingsWorkHours>(
        {
          day: {
            render: (value) => <span>{t(DayOfWeek[value.day])}</span>,
          },
          startTime: {},
          endTime: {},
        },
        t,
      ),
    [t],
  );
  const companyWorkHoursForm = useMemo<ITenantFormLayout<CompanySettingsWorkHours>>(
    () => ({
      day: {
        type: new EnumDropdownElement({
          enumData: DayOfWeek,
        }),
      },
      startTime: {
        type: new TimeElement({}),
      },
      endTime: {
        type: new TimeElement({}),
      },
    }),
    [],
  );

  const reload = useCallback(() => {
    if (companyId) {
      loadModel(companyId).then();
    }
  }, [companyId]);

  useEffect(() => {
    reload();
  }, [companyId]);

  useEffect(() => {
    Axios.get("AppHost").then(({ data }) => setAppHost(data));
  }, []);

  const form = useMemo<ITenantFormLayout<CompanySettingsReturnDto>>(
    () => ({
      images: {
        type: new GroupElement({
          pantheonCompanyImage: {
            type: new CustomRenderElement<CompanySettingsReturnDto>({
              renderer: (dto1) => {
                const intList = dto1?.pantheonCompanyImage;

                if (!intList || intList.length === 0) {
                  return <span>Nema slike</span>;
                }

                const base64Image = `data:image/png;base64,${intList}`;

                return (
                  <img
                    src={base64Image}
                    alt="Pantheon Company Image"
                    style={{ maxWidth: "100%", maxHeight: "300px" }}
                  />
                );
              },
            }),
          },
          pantheonCompanyOnixPhoneSmallImage: {
            type: new BlobElement({}),
          },
        }),
      },
      pantheonCompanyName: {
        type: new TextFieldElement({}),
      },
      alternateMainServerPrefix: {
        type: new TextFieldElement({}),
      },
      tenant: {
        type: new EnumDropdownElement({
          enumData: OnixTenant,
        }),
      },
      pantheonAnDocKeyLenNumber: {
        type: new NumberElement({}),
      },
      pantheonAcCompanyPrtTextA: {
        type: new MultilineTextElement({}),
      },
      pantheonAcCompanyPrtTextB: {
        type: new MultilineTextElement({
          rows: 7,
        }),
      },
      connectionName: {
        type: new TextFieldElement({}),
      },
      companySQLConnectionString: {
        type: new MultilineTextElement({
          rows: 7,
        }),
      },
      support: {
        type: new TitleElement({}),
      },
      supportEmailAddress: {
        type: new TextFieldElement({}),
      },
      supportEmailPassword: {
        type: new TextFieldElement({}),
      },
      supportAutoReplyOnNewTicket: {
        type: new CheckboxElement({}),
        translationKey: "support-auto-reply-on-new-ticket",
      },
      supportAutoReplyOnNewTicketSubject: {
        type: new TextFieldElement({}),
        translationKey: "support-auto-reply-on-new-ticket-subject",
        disabledIf: (dto) => !dto?.supportAutoReplyOnNewTicket,
      },
      supportAutoReplyOnNewTicketBody: {
        type: new RichTextElement({
          minHeight: 320,
        }),
        translationKey: "support-auto-reply-on-new-ticket-body",
        disabledIf: (dto) => !dto?.supportAutoReplyOnNewTicket,
      },
      supportAutoReplyOnNewTicketHelp: {
        type: new CustomRenderElement({
          renderer: () => (
            <Text variant="small">{t("support-auto-reply-placeholders")}</Text>
          ),
        }),
      },
      email: {
        type: new TitleElement({}),
      },
      companyDocumentsEmail: {
        type: new TextFieldElement({}),
      },
      teamViewerScriptKey: {
        type: new TextFieldElement({}),
      },
      teamViewerClientId: {
        type: new TextFieldElement({}),
      },
      teamViewerClientSecret: {
        type: new TextFieldElement({}),
      },
      teamViewerGroupId: {
        type: new CustomSearchBoxElement<
          TeamViewerGroupsDtoGroup,
          CompanySettings
        >({
          customSearchBoxType: "TENANT_OLD",
          key: "id",
          titleKey: "name",
          url: "CompanyTeamViewerGroups",
        }),
      },
      "Current Team Viewer Group ID": {
        type: new TitleElement<any>({
          titleKey: "teamViewerGroupId",
          titleFormatter: (f) => f?.teamViewerGroupId,
        }),
      },
      meshCentral: {
        type: new TitleElement({
          titleKey: "MeshCentral",
        }),
      },
      meshCentralServerUrl: {
        type: new TextFieldElement({}),
      },
      meshCentralUsername: {
        type: new TextFieldElement({
          description:
            "MeshCentral servisni korisnik koji Onix koristi za dohvat grupa i uređaja.",
        }),
      },
      meshCentralPassword: {
        type: new TextFieldElement({
          description:
            "Lozinka servisnog MeshCentral korisnika. Čuva se na razini tenant postavki.",
        }),
      },
      datalab: {
        type: new TitleElement({
          titleKey: "Datalab",
        }),
      },
      datalabSyncEnabled: {
        type: new CheckboxElement({}),
      },
      datalabUsername: {
        type: new TextFieldElement({
          description:
            "Datalab servisni korisnik za sync reference liste i ručne lockdown runove.",
        }),
      },
      datalabPassword: {
        type: new TextFieldElement({
          description: "Lozinka Datalab servisnog korisnika.",
        }),
      },
      datalabBaseUrl: {
        type: new TextFieldElement({
          description:
            "Opcionalni override za Datalab base URL. Ostavite prazno za partner.datalab.eu.",
        }),
      },
      datalabReferencePartnerQuery: {
        type: new TextFieldElement({
          description:
            "Default Reference list partner query. Standardno: Onix informatika.",
        }),
      },
      bookStackUrl: {
        type: new TextFieldElement({}),
      },
      bookStackTokenId: {
        type: new TextFieldElement({}),
      },
      bookStackTokenSecret: {
        type: new TextFieldElement({}),
      },
      additionalBottomTextOnPrint: {
        type: new MultilineTextElement({
          rows: 7,
        }),
      },
      downloadFiscalCertificate: {
        type: new ButtonElement({
          text: "Download Fiscal Certificate",
          onClick: () => {
            DownloadArbitrary(
              "CompanySettings/DownloadFiscalCertificate?companyId=" +
                companyId,
            );
          },
        }),
      },
      fiscCertPassword: {
        type: new TextFieldElement({}),
      },
      useSecondaryCurrency: {
        type: new CheckboxElement({}),
      },
      generatePrivateFiscCert: {
        type: new ButtonElement({
          onClick: () => {
            action("GeneratePrivateFiscCert", undefined, { companyId })
              .then(reload)
              .catch((e) => alert(GetApiError(e)));
          },
          text: "Generiraj privatni ključ fiskalnog certifikata",
          color: (dto as any)?.hasPrivateFiscCert
            ? SharedColors.green10
            : SharedColors.red10,
        }),
      },
      testingFiscalisation: {
        type: new CheckboxElement({}),
      },
      workOrderWorkerPickerIncludesInactiveWorkers: {
        type: new CheckboxElement({}),
      },
      workOrderItemsAdditionalReportData: {
        type: new ButtonElement({
          onClick: () => {
            history(
              AppRoute.WorkOrderItemsAdditionalReportDataEditor(companyId),
            );
          },
          text: t("workOrderItemsAdditionalReportData"),
        }),
      },
      fetchPantheonData: {
        type: new ButtonElement({
          text: "Dohvati Pantheon podatke",
          onClick: () => {
            setLoading(true);
            action("FetchPantheonData", undefined, { companyId })
              .then(() => {
                alert("Uspješno dohvaćeno!");
                reload();
              })
              .catch((e) => alert(GetApiError(e)))
              .finally(() => setLoading(false));
          },
        }),
      },
      migratePantheonDatabase: {
        type: new ButtonElement({
          text: "Migriraj Pantheon bazu",
          onClick: () => {
            setLoading(true);
            action("MigratePantheonDatabase", undefined, { companyId })
              .then(() => {
                alert("Uspješno migrirano!");
                reload();
              })
              .catch((e) => alert(GetApiError(e)))
              .finally(() => setLoading(false));
          },
        }),
      },
      companyStamp: {
        type: new BlobElement({}),
      },
      outboundEmailSettings: {
        type: new TitleElement({}),
      },
      companyOutboundEmail: {
        type: new TextFieldElement({}),
      },
      companyOutboundEmailUser: {
        type: new TextFieldElement({}),
      },
      companyOutboundEmailPassword: {
        type: new TextFieldElement({}),
      },
      companyOutboundEmailHost: {
        type: new TextFieldElement({}),
      },
      companyOutboundEmailPort: {
        type: new NumberElement({}),
      },
      companyOutboundEmailUseSSL: {
        type: new CheckboxElement({}),
      },
      companyOutboundEmailUseTLS: {
        type: new CheckboxElement({}),
      },
      companyOutboundEmailDisplayName: {
        type: new TextFieldElement({}),
      },
      companyOutboundEmailTestButton: {
        type: new ButtonElement({
          text: "Testiraj",
          onClick: () => {
            action("TestOutboundEmail", undefined, {
              companyId,
            })
              .catch((e) => alert(GetApiError(e)))
              .then(() => alert("Uspješno poslano!"));
          },
        }),
      },
      pantheonAlsoUseNDigitBarcodes: {
        type: new NumberElement({}),
      },
      companyGS1Prefix: {
        type: new TextFieldElement({}),
      },
    }),
    [dto, t, companyId, action, reload, history],
  );

  const saveAiSupportSettings = useCallback(
    async (settings: any) => {
      if (!companyId || !model) {
        return;
      }

      setLoading(true);
      try {
        await Axios.put(`AdminSingleCompanyTenant/${companyId}`, {
          ...model,
          ...settings,
        });
        reload();
      } catch (e: any) {
        alert(GetApiError(e?.response?.data ?? e));
      } finally {
        setLoading(false);
      }
    },
    [companyId, model, reload],
  );

  const loadAiSupportModels = useCallback(
    async (apiKey?: string) => {
      if (!companyId) {
        return [];
      }

      const { data } = await Axios.post<string[]>(
        "AdminSingleCompanyTenant/LoadAiSupportModels",
        {
          apiKey: apiKey?.trim() || undefined,
        },
        {
          params: {
            companyId,
          },
        },
      );

      return data ?? [];
    },
    [companyId],
  );

  return (
    <Stack grow style={{ flex: 1, width: "100%" }}>
      {model && (
        <Pivot>
          <PivotItem headerText="Osnovni podaci">
            {companyId && model ? (
              <div
                style={{ overflowY: "scroll", width: "100%", height: "90vh" }}
              >
                <TenantForm
                  inlineTitle={() => (
                    <Text variant={"large"}>Postavke poduzeća</Text>
                  )}
                  reload={() => {}}
                  inline
                  resource={TenantResource.AdminSingleCompanyTenant}
                  addingTitle={"adding-new-company"}
                  editingTitle={"editing-company"}
                  form={form}
                  baseDto={{}}
                  baseModel={model}
                />
              </div>
            ) : null}
          </PivotItem>
          <PivotItem headerText="AI podrška">
            <Stack style={{ paddingTop: 12 }}>
              <CompanyAiSupportSettingsForm
                settings={model}
                save={saveAiSupportSettings}
                loadModels={loadAiSupportModels}
              />
            </Stack>
          </PivotItem>
          <PivotItem headerText="F2, eRačuni">
            {companyId ? <>
              <EInvoiceSettingsForm companyId={companyId} crossCompany={true} onSaved={reload} />
            </> : null}
          </PivotItem>
          <PivotItem headerText="Radni sati">
            {companyId ? (
              <TenantList
                title="Radni sati"
                addingTitle={"Dodaj radne sate" as any}
                editingTitle={"Uredi radne sate" as any}
                form={companyWorkHoursForm}
                defaultGroups={["day"]}
                makeGroupHeaderAddonText={(group) => {
                  const [first] = group;
                  return <p>{t(DayOfWeek[first.day])}</p>;
                }}
                resource={TenantResource.CompanySettingsWorkHours}
                baseDto={companyWorkHoursBaseDto}
                columns={companyWorkHoursColumns}
                queryIsRequired
                query={companyWorkHoursQuery}
                orderBy="day"
              />
            ) : null}
          </PivotItem>
          <PivotItem headerText="Aplikacijski paketi">
            <CompanyApplicationPackets companyId={model.id} />
          </PivotItem>
          <PivotItem headerText="Proslijeđivanje emailova">
            {companyId && <CompanyForwardEmailsSection companyId={companyId} />}
          </PivotItem>
          <PivotItem headerText="Ispisi naljepnica">
            {companyId && (
              <CompanyLabelCompositionsSection companyId={companyId} />
            )}
          </PivotItem>
        </Pivot>
      )}
    </Stack>
  );
};
